import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { loadTranslations } from './i18n';
import { routeModuleMap } from './route-module-map';
import {
  useAvailableLanguages,
  useAvailableModules,
} from '@/components/core/language-selector/hooks/use-language';

/**
 * Type definition for the Language Context.
 * Provides language-related state and functionality throughout the application.
 *
 * @interface LanguageContextType
 */
interface LanguageContextType {
  currentLanguage: string;
  setLanguage: (language: string, isUserAction?: boolean) => Promise<void>;
  /** True only during first app bootstrap — blocks the entire app shell. */
  isInitialLoading: boolean;
  /** True while language APIs or an explicit language switch are in progress. */
  isLoading: boolean;
  availableLanguages: any[];
  availableModules: any[];
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

/**
 * Props for the LanguageProvider component.
 *
 * @interface LanguageProviderProps
 */
interface LanguageProviderProps {
  children: ReactNode;
  defaultLanguage?: string;
  defaultModules?: string[];
}

/**
 * Cache to store loaded translation modules for each language
 * Structure: { [language: string]: Set<string> }
 */
const translationCache: Record<string, Set<string>> = {};

export function LanguageProvider({
  children,
  defaultLanguage = 'en-US',
  defaultModules = ['generic'],
}: Readonly<LanguageProviderProps>) {
  const location = useLocation();
  // Start with a temporary language - will be updated once API default is loaded
  const [currentLanguage, setCurrentLanguage] = useState<string>(defaultLanguage);

  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const { i18n } = useTranslation();
  const { data: languages = [], isLoading: isLanguagesLoading } = useAvailableLanguages();
  const { data: modules = [], isLoading: isModulesLoading } = useAvailableModules();
  const isInitialized = useRef(false);
  const hasCheckedDefaultLanguage = useRef(false);
  const lastApiDefaultLanguage = useRef<string | null>(null);

  useEffect(() => {
    const loading = isLanguagesLoading || isModulesLoading;
    setIsLoading(loading);
    if (loading && !isInitialized.current) {
      setIsInitialLoading(true);
    }
  }, [isLanguagesLoading, isModulesLoading]);

  /**
   * Resolves the translation modules for a pathname by longest-prefix match
   * against `routeModuleMap`. E.g. for '/hr/directory/123', it tries
   * '/hr/directory/123', then '/hr/directory', then '/hr', falling back to
   * `defaultModules` if none of those prefixes have an entry. This lets
   * route-module-map.ts register routes at whatever segment depth matches
   * how translations are actually split on the backend (e.g. a specific
   * '/hr/directory' entry instead of one catch-all '/hr' entry), while a
   * broader entry (like '/hr') still catches any route nested under it that
   * has no more specific entry of its own.
   *
   * @param {string} pathname - The full pathname from the router
   * @returns {string[]} The matched module names
   */
  const getMatchedModules = useCallback(
    (pathname: string): string[] => {
      const segments = pathname.split('/').filter(Boolean);
      for (let i = segments.length; i > 0; i--) {
        const candidate = '/' + segments.slice(0, i).join('/');
        if (routeModuleMap[candidate]) {
          return routeModuleMap[candidate];
        }
      }
      return defaultModules;
    },
    [defaultModules]
  );

  /**
   * Checks if all required modules for a route are cached
   */
  const areModulesCached = useCallback((language: string, modules: string[]): boolean => {
    if (!translationCache[language]) return false;
    return modules.every((module) => translationCache[language].has(module));
  }, []);

  /**
   * Adds loaded modules to the cache
   */
  const cacheModules = useCallback((language: string, modules: string[]): void => {
    if (!translationCache[language]) {
      translationCache[language] = new Set();
    }
    modules.forEach((module) => translationCache[language].add(module));
  }, []);

  /**
   * Loads translation modules for a given language and route.
   * Determines required modules based on the current route and loads their translations.
   *
   * @param {string} language - Language code to load translations for
   * @param {string} pathname - Current route pathname
   * @returns {Promise<void>} Resolves when all modules are loaded
   */
  const loadLanguageModules = useCallback(
    async (language: string, pathname: string) => {
      const matchedModules = getMatchedModules(pathname);

      if (areModulesCached(language, matchedModules)) {
        return;
      }

      for (const moduleName of matchedModules) {
        try {
          if (!translationCache[language]?.has(moduleName)) {
            await loadTranslations(language, moduleName);
            cacheModules(language, [moduleName]);
          }
        } catch (err) {
          console.error(`Failed to load translations for module ${moduleName}:`, err);
        }
      }
    },
    [getMatchedModules, areModulesCached, cacheModules]
  );

  /**
   * Changes the application's active language.
   * - Persists the language choice to localStorage
   * - Loads required translation modules
   * - Updates i18n instance and context state
   *
   * @param {string} language - The language code to switch to
   * @param {boolean} isUserAction - Whether this is triggered by user action (default: true)
   * @returns {Promise<void>} Resolves when language change is complete
   */
  const setLanguage = useCallback(
    async (language: string, isUserAction = true): Promise<void> => {
      setIsLoading(true);
      try {
        if (typeof window !== 'undefined') {
          localStorage.setItem('language', language);
          // Mark if this was an explicit user selection
          if (isUserAction) {
            localStorage.setItem('language_user_selected', 'true');
          }
        }

        await loadLanguageModules(language, location.pathname);
        i18n.changeLanguage(language);
        setCurrentLanguage(language);
        isInitialized.current = true; // Mark as initialized after language change
      } catch (error) {
        console.error('Failed to change language:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [loadLanguageModules, location.pathname, i18n]
  );

  /**
   * Effect to check and apply the default language from the API.
   * This runs when languages are loaded and automatically detects when the
   * API's default language changes, applying it unless user has explicitly chosen a language.
   */
  useEffect(() => {
    if (isLanguagesLoading || languages.length === 0) {
      return;
    }

    const storedLanguage = typeof window !== 'undefined' ? localStorage.getItem('language') : null;
    const userExplicitChoice =
      typeof window !== 'undefined' ? localStorage.getItem('language_user_selected') : null;
    const apiDefaultLanguage = languages.find((lang) => lang.isDefault);

    if (!apiDefaultLanguage) {
      return;
    }

    const apiDefaultCode = apiDefaultLanguage.languageCode;

    // Detect if the API default language has changed
    const apiDefaultChanged =
      lastApiDefaultLanguage.current !== null && lastApiDefaultLanguage.current !== apiDefaultCode;

    // Update the last known API default
    lastApiDefaultLanguage.current = apiDefaultCode;

    // If user has explicitly selected a language, only override if API default changed
    if (userExplicitChoice === 'true' && storedLanguage && !apiDefaultChanged) {
      const storedLanguageExists = languages.some((lang) => lang.languageCode === storedLanguage);
      if (
        storedLanguageExists &&
        storedLanguage !== currentLanguage &&
        hasCheckedDefaultLanguage.current
      ) {
        setLanguage(storedLanguage, false);
        return;
      }
    }

    // Apply the API's default language if:
    // 1. First time checking (initial load)
    // 2. API default has changed
    // 3. No user explicit choice exists
    if (!hasCheckedDefaultLanguage.current || apiDefaultChanged || userExplicitChoice !== 'true') {
      if (apiDefaultCode !== currentLanguage) {
        setLanguage(apiDefaultCode, false);
      }
      hasCheckedDefaultLanguage.current = true;
    }
  }, [languages, isLanguagesLoading, currentLanguage, setLanguage]);

  /**
   * Effect hook to initialize translations when the component mounts.
   * Loads initial translation modules and sets up the language.
   * Waits for languages to be loaded to ensure we use the correct default language.
   */
  useEffect(() => {
    // Don't initialize until languages are loaded and default language check is complete
    if (isInitialized.current || isLanguagesLoading || !hasCheckedDefaultLanguage.current) {
      return;
    }

    const initializeTranslations = async () => {
      setIsLoading(true);
      setIsInitialLoading(true);
      try {
        await loadLanguageModules(currentLanguage, location.pathname);
        i18n.changeLanguage(currentLanguage);
        isInitialized.current = true;
      } catch (error) {
        console.error('Failed to initialize translations:', error);
      } finally {
        setIsLoading(false);
        setIsInitialLoading(false);
      }
    };

    initializeTranslations();
  }, [currentLanguage, location.pathname, loadLanguageModules, i18n, isLanguagesLoading]);

  /**
   * Effect hook to handle route changes.
   * Loads required translation modules when the route or language changes.
   */
  useEffect(() => {
    const loadOnRouteChange = async () => {
      try {
        await loadLanguageModules(currentLanguage, location.pathname);
        // Re-render translated content without unmounting the app shell.
        i18n.changeLanguage(i18n.language);
      } catch (err) {
        console.error('Failed to load modules on route change:', err);
      }
    };

    loadOnRouteChange();
  }, [currentLanguage, location.pathname, loadLanguageModules, i18n]);

  const value = useMemo(
    () => ({
      currentLanguage,
      setLanguage,
      isInitialLoading,
      isLoading,
      availableLanguages: languages,
      availableModules: modules,
    }),
    [currentLanguage, setLanguage, isInitialLoading, isLoading, languages, modules]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export const useLanguageContext = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguageContext must be used within a LanguageProvider');
  }
  return context;
};
